import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { useAuth } from '../../useAuth';
import { buildLoginUrlWithReturn } from '../../utils/returnTo';
import { useWizardIgWizard } from '../../hooks/wizardIg/useWizardIgWizard';
import WizardIgService from '../../services/wizardIg.service';
import logo from '../../assets/Logo.webp';
import logoText from '../../assets/logo-text-only.webp';
import WizardBackdrop from './WizardBackdrop';
import SidebarSilk from './SidebarSilk';
import { WizButton } from './wizardUi';
import Step1AgentType from './steps/Step1AgentType';
import Step2BusinessInfo from './steps/Step2BusinessInfo';
import Step3Payments from './steps/Step3Payments';
import Step4UseCases from './steps/Step4UseCases';
import Step5BusinessPolicies from './steps/Step5BusinessPolicies';
import Step6Interview from './steps/Step6Interview';
import Step7Build from './steps/Step7Build';

const STEP_COMPONENTS = [
  Step1AgentType,
  Step2BusinessInfo,
  Step3Payments,
  Step4UseCases,
  Step5BusinessPolicies,
  Step6Interview,
  Step7Build,
];

const RAIL_KEYS = ['rail0', 'rail1', 'rail2', 'rail3', 'rail4', 'rail5', 'rail6'];

// Pasos 0-4 muestran Atras/Continuar en el footer. La entrevista (5) avanza
// sola desde el chat y el build (6) no tiene botones.
const FOOTER_STEPS = [0, 1, 2, 3, 4];

function isStepValid(wizard) {
  switch (wizard.step) {
    case 0:
      return wizard.agentType === 'builtin' || wizard.agentOtherDescription.trim().length > 0;
    case 1:
      return wizard.business.name.trim().length > 0;
    case 2:
      return Object.values(wizard.payments).some((p) => p.enabled);
    default:
      return true;
  }
}

function LangToggle({ i18n }) {
  return (
    <div className="inline-flex gap-0.5 rounded-full border border-white/10 bg-white/[0.04] p-1">
      {['es', 'en'].map((lng) => (
        <button
          key={lng}
          type="button"
          onClick={() => i18n.changeLanguage(lng)}
          className={`rounded-full px-4 py-1.5 text-[12px] font-semibold uppercase tracking-wide transition-colors ${
            i18n.resolvedLanguage === lng
              ? 'bg-primary text-primary-foreground shadow-[0_0_14px_rgba(124,58,237,0.5)]'
              : 'text-white/55 hover:text-white'
          }`}
        >
          {lng}
        </button>
      ))}
    </div>
  );
}

export default function WizardIgView() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const wizard = useWizardIgWizard();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      const returnTo = '/wizard-ig' + (location.search || '');
      navigate(buildLoginUrlWithReturn(returnTo), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated]);

  if (isLoading || !isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" />
          <p className="text-sm text-muted-foreground">{t('wizardIg.authRedirecting')}</p>
        </div>
      </div>
    );
  }

  async function handleContinue() {
    if (!isStepValid(wizard)) return;
    setSaving(true);
    try {
      if (wizard.step === 1) await WizardIgService.saveBusinessInfo(wizard.business);
      if (wizard.step === 2) await WizardIgService.savePaymentMethods(wizard.payments);
      if (wizard.step === 3) await WizardIgService.saveUseCase(wizard.orders);
      if (wizard.step === 4) await WizardIgService.savePolicies(wizard.policies);
    } finally {
      setSaving(false);
      wizard.next();
    }
  }

  const StepComponent = STEP_COMPONENTS[wizard.step];
  const showFooter = FOOTER_STEPS.includes(wizard.step);
  const canContinue = isStepValid(wizard) && !saving;

  const startOver = wizard.step > 0 && (
    <button
      type="button"
      onClick={wizard.reset}
      className="text-[12px] font-medium text-white/45 underline-offset-2 transition-colors hover:text-white hover:underline"
    >
      {t('wizardIg.startOver')}
    </button>
  );

  return (
    <div className="relative min-h-screen w-full overflow-hidden text-foreground">
      <WizardBackdrop />

      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-3 py-5 sm:px-6 lg:px-10 lg:py-8">
        {/* Ventana: alto fijo, el contenido hace scroll adentro y el footer no se mueve */}
        <div className="relative flex h-[min(780px,calc(100dvh_-_72px))] w-full max-w-md flex-col overflow-hidden rounded-[28px] border border-white/[0.11] bg-[#0e0a1c] shadow-[0_40px_120px_rgba(0,0,0,0.65),inset_0_1px_0_rgba(255,255,255,0.09),inset_0_0_0_1px_rgba(255,255,255,0.02)] lg:h-[min(800px,calc(100dvh_-_96px))] lg:min-h-[560px] lg:max-w-[1240px] lg:flex-row"
          style={{
            backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.022) 36%, rgba(255,255,255,0) 64%)',
          }}
        >
          {/* Barra lateral -- solo desktop */}
          <aside className="hidden w-[272px] shrink-0 flex-col border-r border-white/[0.07] bg-white/[0.015] p-6 lg:flex">
            <div className="mt-1 flex items-center gap-3">
              <img src={logo} alt="Zenit" className="h-9 w-auto" />
              <img src={logoText} alt="Zenit" className="h-[18px] w-auto" />
            </div>

            <nav className="mt-8 flex flex-col gap-1.5" aria-label="Wizard">
              {RAIL_KEYS.map((key, i) => {
                const state = i < wizard.step ? 'done' : i === wizard.step ? 'active' : 'pending';
                return (
                  <div
                    key={key}
                    aria-current={state === 'active' ? 'step' : undefined}
                    className={`flex items-center gap-3 rounded-2xl px-3 py-2.5 text-[14px] transition-colors ${
                      state === 'active'
                        ? 'bg-primary/20 font-medium text-white'
                        : state === 'done'
                          ? 'text-white/70'
                          : 'text-white/45'
                    }`}
                  >
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold ${
                        state === 'active'
                          ? 'bg-primary text-white shadow-[0_0_18px_rgba(124,58,237,0.65)]'
                          : state === 'done'
                            ? 'bg-white/10 text-white/85'
                            : 'bg-white/[0.06] text-white/50'
                      }`}
                    >
                      {state === 'done' ? <Check size={14} strokeWidth={2.6} /> : i + 1}
                    </span>
                    {t(`wizardIg.${key}`)}
                  </div>
                );
              })}
            </nav>

            {startOver && <div className="mt-5 px-3">{startOver}</div>}

            <SidebarSilk />
          </aside>

          <section className="flex min-h-0 min-w-0 flex-1 flex-col">
            {/* Cabecera mobile */}
            <div className="flex items-center justify-between px-5 pt-5 lg:hidden">
              <div className="flex items-center gap-2.5">
                <img src={logo} alt="Zenit" className="h-8 w-auto" />
                <img src={logoText} alt="Zenit" className="h-4 w-auto" />
              </div>
              <LangToggle i18n={i18n} />
            </div>
            <div className="flex gap-1.5 px-5 pt-4 lg:hidden">
              {Array.from({ length: wizard.totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-colors ${i <= wizard.step ? 'bg-primary' : 'bg-white/10'}`}
                />
              ))}
            </div>

            {/* Toggle de idioma -- desktop */}
            <div className="hidden justify-end px-10 pt-7 lg:flex">
              <LangToggle i18n={i18n} />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-5 lg:px-10 lg:pt-1">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={wizard.step}
                  className="h-full"
                  initial={{ opacity: 0, y: 10, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, y: -10, filter: 'blur(4px)' }}
                  transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                >
                  <StepComponent wizard={wizard} />
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Footer siempre presente (vacio en entrevista/build) para que la
                ventana no cambie de alto entre pasos. */}
            <div className="flex shrink-0 items-center justify-between gap-3 border-t border-white/[0.07] px-5 py-4 lg:px-10 lg:py-5">
              {showFooter ? (
                <>
                  {wizard.step > 0 ? (
                    <WizButton variant="ghost" onClick={wizard.back} disabled={saving}>
                      <ArrowLeft size={17} />
                      {t('wizardIg.btnBack')}
                    </WizButton>
                  ) : (
                    <span />
                  )}
                  <WizButton onClick={handleContinue} disabled={!canContinue} className="min-w-[150px] flex-1 lg:flex-none">
                    {t('wizardIg.btnContinue')}
                    <ArrowRight size={17} />
                  </WizButton>
                </>
              ) : (
                <div className="h-[52px] w-full" />
              )}
            </div>
          </section>
        </div>

        {startOver && <div className="mt-3 lg:hidden">{startOver}</div>}
      </div>
    </div>
  );
}
